import React, { useCallback, useState } from 'react'
import classNames from 'classnames'
import { MoreStoriesButton } from 'src/organisms/CollectibleSceneInfo/MoreStoriesButton'
import { CaptionSM } from '@/atoms/Text'
import { useMomentStoryModal } from '@/organisms/CollectibleSceneInfo/MomentStoryModal'
import { StoryCard } from '@/organisms/CollectibleSceneInfo/StoryCard'
import { useViewableStoriesConfig } from '@/organisms/CollectibleSceneInfo/useViewableStoriesConfig'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { MomentUserStory } from '@/services/MomentsService/types'
import { useUser } from '@/services/UserService'
import { isTextStory } from '@/utils/StoryUtil/StoryUtil'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { getUserDisplayName } from '@/utils/users'

interface CollectibleSceneStoriesProps {
  stories?: MomentUserStory[] | null
  totalStories: number
  hideMoreStoriesButton?: boolean
}

export const CollectibleSceneStories: React.FC<CollectibleSceneStoriesProps> = ({
  stories = [],
  totalStories,
  hideMoreStoriesButton = false,
}) => {
  const { t } = useTranslate('watch')
  const { onOpen: onOpenModal } = useMomentStoryModal()
  const { userId } = useUser()
  const { viewableCount, remainingCount } = useViewableStoriesConfig(totalStories)
  const { userStoryId } = useCollectiblesPlayerEngagement()

  const [isInit, setIsInit] = useState(false)

  if (!isInit && userStoryId && stories?.length) {
    const story = stories.find((story) => story?.id === userStoryId)
    if (story) {
      const sharedStory = {
        id: story.id,
        userId: story.user?.id as string,
        text: story.text,
        videoUri: story.videoUri,
        isMyStory: Boolean(userId && story?.user?.id === userId),
        userAvatar: story?.user?.profile?.image,
        userName: getUserDisplayName(story?.user, t('anonymous', 'Anonymous')),
        publishDate: story?.publishDate,
        totalLikes: story?.totalLikes,
        isLikedByUser: story?.isLikedByUser,
        momentId: story?.momentId,
      }

      onOpenModal({
        ...sharedStory,
        id: sharedStory.id || undefined,
      })
    }
    setIsInit(true)
  }

  const handleOnOpen = useCallback(
    (story: NonNullable<MomentUserStory>) => {
      const storyToOpen = {
        id: story.id,
        userId: story.user?.id as string,
        text: story.text,
        videoUri: story.videoUri,
        isMyStory: Boolean(userId && story?.user?.id === userId),
        userAvatar: story?.user?.profile?.image,
        userName: getUserDisplayName(story?.user, t('anonymous', 'Anonymous')),
        publishDate: story?.publishDate,
        totalLikes: story?.totalLikes,
        isLikedByUser: story?.isLikedByUser,
        momentId: story?.momentId,
      }

      onOpenModal({
        ...storyToOpen,
        id: storyToOpen.id || undefined,
      })
    },
    [onOpenModal, t, userId],
  )

  if (!stories?.length) return null
  const viewableStories = stories.slice(0, viewableCount)
  const hasVideoStory = viewableStories.some((story) => story?.videoUri)

  return (
    <div className="mb-6 w-full rounded-xl bg-gray-800 p-4">
      <CaptionSM color="gray-500" className="mb-2">
        <Translate i18nKey="videoStoriesstories" t={t}>
          Video Stories:
        </Translate>
      </CaptionSM>

      <div className="relative flex w-full grow-1 items-center gap-2 ">
        {viewableStories?.map((story) => {
          const typeCheck = { text: story?.text, videoUri: story?.videoUri }

          return (
            story && (
              <div key={story.id}>
                <StoryCard
                  className={classNames({
                    'border border-core-gray-800 bg-core-gray-900': isTextStory(typeCheck),
                    'w-20': remainingCount >= 0,
                  })}
                  story={story}
                  onClick={() => handleOnOpen(story)}
                  hasVideoStory={hasVideoStory}
                />
              </div>
            )
          )
        })}
        {!hideMoreStoriesButton && remainingCount > 0 && <MoreStoriesButton totalNumberOfStories={totalStories} />}
      </div>
    </div>
  )
}
